import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Layout from "../components/layout/Layout";
import ProductCard from "../components/ProductCard";
import products from "../data/products";
import useSeo from "../hooks/useSeo";

const STEPS = [
  { n: "01", label: "Välj lösning" },
  { n: "02", label: "Se pris" },
  { n: "03", label: "Skicka förfrågan" },
  { n: "04", label: "Vido bekräftar förutsättningarna" },
  { n: "05", label: "Installation" },
];

const CITIES = ["Lidköping", "Götene", "Skara"];

const PILLARS = [
  {
    title: "Fast pris från start",
    body: "Du vet vad installationen kostar innan vi bokar tid — inga öppna timkonton.",
  },
  {
    title: "Personlig kontakt",
    body: "Du pratar med samma person hela vägen, från förfrågan till installation.",
  },
  {
    title: "Lokal installatör",
    body: "Vi finns i Lidköping, Götene och Skara — och kör aldrig långt för ett servicebesök.",
  },
];

export default function Start() {
  useSeo({
    title: "Vido Klimatteknik | Värme & klimat i Lidköping",
    description:
      "Fasta priser på värmepumpar, AC, varmvattenberedare och ventilation för privatpersoner i Lidköping, Götene och Skara.",
    path: "/",
  });
  return (
    <Layout>
      {/* HERO */}
      <section
        data-testid="start-hero"
        className="relative overflow-hidden"
      >
        <div className="container-vido pt-12 md:pt-16 pb-14 md:pb-20 grid gap-10 lg:gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6 fade-in">
            <span
              className="eyebrow"
              data-testid="hero-eyebrow"
            >
              Lidköping · Götene · Skara
            </span>
            <h1
              className="display-h1 mt-5"
              data-testid="hero-h1"
            >
              Fasta priser.
              <br />
              <span className="italic text-[color:hsl(var(--accent-terracotta))]">
                Installerat
              </span>{" "}
              och klart.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[color:hsl(var(--muted-ink))] max-w-[52ch] leading-relaxed">
              Vido hjälper privatpersoner i Lidköping, Götene och Skara med
              moderna lösningar för värme, kyla och ett bättre inomhusklimat.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/produkter"
                data-testid="hero-cta-primary"
                className="btn-primary"
              >
                Se våra lösningar
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/kontakt"
                data-testid="hero-cta-secondary"
                className="btn-secondary"
              >
                Kontakta Vido
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 fade-in">
            <div className="relative">
              <div className="aspect-[4/5] md:aspect-[5/6] lg:aspect-[4/5] w-full overflow-hidden rounded-[28px] border border-[color:hsl(var(--line))] bg-[hsl(var(--card-bg))] shadow-[0_30px_60px_-30px_rgba(31,29,27,0.25)]">
                <img
                  src="/products/hero-villa.png"
                  alt="Ljus modern nordisk villa med naturligt ljus"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="hidden md:block absolute -left-6 -bottom-6 bg-white border border-[color:hsl(var(--line))] rounded-2xl px-5 py-4 shadow-[0_10px_30px_-15px_rgba(31,29,27,0.25)]">
                <div className="eyebrow">Fast pris efter ROT</div>
                <div className="mt-1 font-serif text-2xl font-medium">
                  Från 18 900 kr
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section
        id="produkter"
        data-testid="start-products"
        className="section-y bg-[hsl(var(--surface))]"
      >
        <div className="container-vido">
          <div className="max-w-3xl fade-in">
            <span className="eyebrow">Våra lösningar</span>
            <h2 className="display-h2 mt-4">
              Välj lösning och se pris direkt
            </h2>
            <p className="mt-4 text-[color:hsl(var(--muted-ink))] text-base md:text-lg max-w-[58ch]">
              Fem tydliga paket för värme, varmvatten, kyla och ventilation —
              med fast pris efter ROT.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
            {products.map((p, i) => (
              <div
                key={p.slug}
                className={`lg:col-span-2 ${i === 3 ? "lg:col-start-2" : ""}`}
              >
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TYDLIGT FRÅN BÖRJAN */}
      <section
        data-testid="start-steps"
        className="section-y"
      >
        <div className="container-vido">
          <div className="max-w-3xl fade-in">
            <span className="eyebrow">Så fungerar det</span>
            <h2 className="display-h2 mt-4">Tydligt från början</h2>
            <p className="mt-4 text-[color:hsl(var(--muted-ink))] text-base md:text-lg max-w-[58ch]">
              Från första klick till färdig installation — utan öppna
              kostnader och utan att du behöver jaga oss.
            </p>
          </div>

          <ol className="mt-10 grid gap-4 md:grid-cols-5">
            {STEPS.map((s, i) => (
              <li
                key={s.n}
                data-testid={`step-${i + 1}`}
                className="fade-in card-vido p-5 flex flex-col gap-3 min-h-[120px]"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span className="font-serif text-2xl text-[color:hsl(var(--accent-terracotta))] font-medium">
                  {s.n}
                </span>
                <span className="text-sm md:text-base font-medium text-[color:hsl(var(--ink))] leading-snug">
                  {s.label}
                </span>
              </li>
            ))}
          </ol>

          <div className="mt-8">
            <Link
              to="/sa-fungerar-det"
              data-testid="steps-cta"
              className="btn-secondary"
            >
              Läs mer om hur det fungerar
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* GEOGRAFI */}
      <section
        data-testid="start-geo"
        className="section-y bg-white border-y border-[color:hsl(var(--line))]"
      >
        <div className="container-vido">
          <div className="grid gap-8 lg:grid-cols-12 items-end">
            <div className="lg:col-span-6 fade-in">
              <span className="eyebrow">Lokalt</span>
              <h2 className="display-h2 mt-4">
                Vi finns för dig i Lidköping, Götene och Skara
              </h2>
              <p className="mt-4 text-[color:hsl(var(--muted-ink))] text-base md:text-lg max-w-[52ch]">
                Kort restid, personlig kontakt och installatörer som känner
                området. Om något behöver justeras efteråt är vi aldrig långt
                borta.
              </p>
            </div>
            <div className="lg:col-span-6 grid gap-4 sm:grid-cols-3">
              {CITIES.map((c, i) => (
                <div
                  key={c}
                  data-testid={`geo-city-${c.toLowerCase()}`}
                  className="fade-in card-vido p-5 flex flex-col gap-3"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <MapPin
                    size={20}
                    className="text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <div className="font-serif text-2xl font-medium leading-tight">
                    {c}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRYGGHET */}
      <section
        data-testid="start-trust"
        className="section-y"
      >
        <div className="container-vido">
          <div className="max-w-3xl fade-in">
            <span className="eyebrow">Så gör Vido</span>
            <h2 className="display-h2 mt-4">
              Enkelt, tydligt och personligt
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className="fade-in card-vido p-6"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-8 h-[2px] bg-[color:hsl(var(--accent-terracotta))] mb-5" />
                <h3 className="font-serif text-2xl font-medium leading-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-[color:hsl(var(--muted-ink))] leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SLUT-CTA */}
      <section
        data-testid="start-final-cta"
        className="py-12 md:py-14"
      >
        <div className="container-vido">
          <div className="rounded-[28px] border border-[color:hsl(var(--line))] bg-gradient-to-br from-white to-[hsl(var(--surface))] py-12 md:py-14 px-8 md:px-16 text-center relative overflow-hidden fade-in">
            <div
              aria-hidden
              className="absolute inset-x-0 -top-24 h-56 bg-[color:hsl(var(--accent-terracotta))]/8 blur-3xl"
            />
            <span className="eyebrow relative">Nästa steg</span>
            <h2 className="display-h2 mt-4 relative">
              Redo att ta nästa steg?
            </h2>
            <p className="mt-4 text-[color:hsl(var(--muted-ink))] max-w-[52ch] mx-auto relative">
              Berätta lite om ditt hem så återkommer Vido med förutsättningar
              och tid för installation.
            </p>
            <div className="mt-8 relative">
              <Link
                to="/kontakt"
                data-testid="final-cta"
                className="btn-primary"
              >
                Skicka förfrågan
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
